import { isEmpty, isPresent } from '@ember/utils';
const PHONE_REGEXES = {
  // 9 digits with leading 0, 1, or 61
  AU: /((^61|0|1)\d{9}$|^1\d{5}$)/,
  // 10 digits, plus maybe a leading 1
  CA: /^1?\d{10}$/,
  // 8 or 9 digits starting with 44 or 0 following by any digit NOT 0, 4 or 6,
  // or 6 digits starting with 44 or 0 following by number 8
  // Source: https://www.area-codes.org.uk/formatting.php
  GB: /^((44|0)[1-357-9]\d{8,9})|((448|08)\d{6})$/,
  // 10 digits, plus maybe a leading 1
  US: /^1?\d{10}$/,
  // 9 to 12 digits
  JP: /^(81|0)?\d{9,12}$/,
  // optional country code 353 followed by 2-4 area code digits and 5-7 subscriber digits
  // mobile numbers start with 08 followed by 8 digits, but are covered with the landline regex
  // https://docs.google.com/document/d/1d4mUoUmz5WcIOf94QVjhPZbEPYBW4qQOeWKRx6nxbuA/edit#heading=h.87ckkw2olu36
  IE: /^(353)?\d{7,11}$/,
  // French phone numbers are made of 10 digits written as 5 series of 2 numbers. The first series always starts with 0
  // followed by a digit ranging 1-9.
  // Example: 01 12 34 56 78
  // Numbers starting with 08 can be written in a different format. Example: 0 812 345 678
  // https://docs.google.com/document/d/17Tg8-mHy4AntUUJWJWUmIbFpkRIexpvO0w05AbBFbWo/edit#heading=h.1phcod2zq7zz
  FR: /^(0[1-9])(\d{8})$/
};

// Allowed characters regex for business and trading names
//
// See PRD
// https://docs.google.com/document/d/1JZ8pq3WwjjpHW8qc-M4SPCo5Tf-a5ow2gsqEcoq1NrI/edit?tab=t.0
//
// See slack thread
// https://square.slack.com/archives/C07N00NUYQN/p1747349815996049
//
// Any letter in any language with diacritics allowed (\p{L})
// Numbers (0-9)
// Space
// Symbols !"#$%&'‘’“”()*+,-./:;@[]\^_`{}|~
const BUSINESS_NAME_REGEX = new RegExp('^[\\p{L}0-9!"#$%&\'\'\'()*+,\\-./:;?@\\[\\]\\\\^_`{}|~"" ]+$', 'u');

// Japan-specific regex that includes CJK punctuation (\u3000-\u303F)
const BUSINESS_NAME_REGEX_JP = new RegExp('^[\\p{L}0-9!"#$%&\'\'\'()*+,\\-./:;?@\\[\\\\\\]^_`{}|~"" \\u3000-\\u303F\\u30FB]+$', 'u');
export function isValidBusinessName(value, countryCode) {
  if (countryCode === 'JP') {
    return BUSINESS_NAME_REGEX_JP.test(value);
  } else {
    return BUSINESS_NAME_REGEX.test(value);
  }
}

/**
 * @deprecated -- use isPhone(value, countryCode) instead
 */

export function isPhone(value, currentFormatsOrCountryCode) {
  if (currentFormatsOrCountryCode instanceof Object) {
    // Deprecated. This is only here for avoiding a breaking change.
    const regexp = new RegExp(currentFormatsOrCountryCode.phone.regexp);
    return regexp.test(value?.replace?.(/\D/g, ''));
  } else {
    const regexp = PHONE_REGEXES[currentFormatsOrCountryCode.toUpperCase()] ??
    // If unknown, simply match any number
    /^\d+$/;
    return regexp.test(value?.replace?.(/\D/g, ''));
  }
}
export function isEmail(value) {
  // http://stackoverflow.com/a/46181/11236
  return /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\dA-Za-z-]+\.)+[A-Za-z]{2,}))$/.test(value);
}
export const isTrue = Boolean;
export function isFalse(value) {
  return !value;
}
export function isNotNegative(value) {
  return value >= 0;
}
export function isPositive(value) {
  return value > 0;
}
export function isInRange(value, min, max) {
  return isPresent(value) && value >= min && value <= max;
}
export function isTin(value) {
  return /^((\d{9})|(\d{3}-\d{2}-\d{4})|(\d{2}-\d{7}))$/.test(value);
}
export function isTinName(value) {
  // IRS rules allow only alphanumeric characters, ampersands and dashes when
  // validating a TIN with a business name
  return /^[\d &a-z-]+$/i.test(value);
}
export function isValidHours(hours) {
  return hoursAreAllDefined(hours) && hoursDoNotOverlap(hours);
}
export function specialHoursDoNotOverlap(specialHours) {
  const invalidHours = specialHours.filter(hour => {
    return specialHours.any(otherHour => hour.doesOverlapWith(otherHour) && !(hour === otherHour));
  });
  return isEmpty(invalidHours);
}
export function hoursAreAllDefined(hours) {
  const invalidHours = hours.filter(hour => hour.isNotDefined());
  invalidHours.forEach(invalidHour => invalidHour.setProperties({
    invalidStartTime: true,
    invalidEndTime: true
  }));
  return isEmpty(invalidHours);
}
export function hoursDoNotOverlap(hours) {
  const invalidHours = hours.filter(hour => {
    return hours.any(otherHour => hour.doesOverlapWith(otherHour) && !(hour === otherHour));
  });
  invalidHours.forEach(invalidHour => invalidHour.setProperties({
    invalidStartTime: true,
    invalidEndTime: true
  }));
  return isEmpty(invalidHours);
}
export function isValidInstagram(value) {
  return /(?=^[\w.]{1,30}$)^(?!.*(^\d*$|^\.|\.$|\.{2,}|instagram|\.(edu|com|net|org|gov|mil|int)$|^www\.)).*/i.test(value);
}
export function isValidNonGifImage(image) {
  const imageType = image && image.type;
  return Boolean(!imageType || imageType.match('image/jpeg') || imageType.match('image/png') || imageType.match('image/jpg'));
}
export function isValidImage(image) {
  if (isValidNonGifImage(image)) {
    return true;
  }
  const imageType = image && image.type;
  return Boolean(!imageType || imageType.match('image/gif'));
}
export function isAlphanumericSpace(value) {
  return /^[\d a-z]+$/i.test(value);
}
export function isValidMerchantSlug(value) {
  return /^[\w-]+$/i.test(value);
}
export function isValidFractionalQuantity(value) {
  // The regex here allows one or more digits, followed by an optional decimal point, followed by one or more digits.
  return Boolean(/^\d+(\.\d+)?$/.test(String(value))) && Number.parseFloat(value) > 0;
}

// matches various English & Spanish synonyms for PO boxes; does not match mid-word keywords e.g. "Robinhood St"
const PO_BOX_REGEX = /^(.*[^a-zÀ-ÖØ-öø-ÿ])?(p(\s|\.)*o(\s|\.)*box(\s|\.)*|gpo\s*box|lock\s*box|call\s*box|box|bin|(firm)?\s*caller|drawer|apartado|aptdo|buzon)([^a-zÀ-ÖØ-öø-ÿ].*)?$/i;
export function isPoBox(value) {
  return PO_BOX_REGEX.test(value);
}
export function isNotPoBox(value) {
  return !isPoBox(value);
}
export function isUsPostalCode(value) {
  return /^\d{5}(?:[\s-]?\d{4})?$/.test(value);
}

// A country in this list does not require an adminLevel1 field to be considered valid
// See https://github.com/squareup/java/blob/master/payments/common/src/main/java/com/squareup/payments/common/PaymentsAddressHelper.java
const ADMINISTRATIVE_DISTRICT_SKIPLIST = new Set(['GB', 'PR', 'VI', 'FR']);
// A country in this list requires an addressLine1 field to be considered valid
const ADDRESS_LINE_1_REQUIRED_ENFORCELIST = new Set(['IE']);
export function isInvalidAddress(addressProperties) {
  if (!ADMINISTRATIVE_DISTRICT_SKIPLIST.has(addressProperties.countryCode) && isEmpty(addressProperties.adminLevel1)) {
    return true;
  }
  if (ADDRESS_LINE_1_REQUIRED_ENFORCELIST.has(addressProperties.countryCode) && isEmpty(addressProperties.addressLine1)) {
    return true;
  }
  return isEmpty(addressProperties.locality) || isEmpty(addressProperties.postalCode);
}